import React, {useRef, useState} from "react";

import {CustomerInterface} from "../../../service/Business/Interfaces/CustomerInterface";
import {AddCustomer, UpdateCustomer} from "../../../service/Business/CustomerService";
import {getIdWithToken} from "../../../utils/storage/Token";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../../Dashboard/Form/ModalForm";
import {ContactInterface} from "../../../service/Business/Interfaces/ContactInterface";
import {
    AddContact,
    DeleteContact, UpdateContact,
    useFetchContacts
} from "../../../service/Business/ContactService";
import FCSpinner from "../../FCObjets/FCSpinner";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import TableContactCustomer from "./Tables/TableContactCustomer";
import {useToast} from "../../FCObjets/FCToast/FCToast";
import "./FormAtelier.scss";

type FormCustomerListProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL,
    dataCustomer: CustomerInterface | undefined;
};

const FormCustomer: React.FunctionComponent<FormCustomerListProps> = ({dataCustomer, onFinished, onError}) => {
    const toast = useToast();

    const {data: contacts, loading, error} = useFetchContacts(dataCustomer ? dataCustomer.id : 0);

    const [inputName, setInputName] = useState(dataCustomer ? dataCustomer.companyName : "");
    const [inputSiret, setSiret] = useState(dataCustomer ? dataCustomer.siret : "");
    const [inputAddress, setAddress] = useState(dataCustomer ? dataCustomer.address : "");
    const [inputLat, setLat] = useState(dataCustomer ? dataCustomer.lat : 0);
    const [inputLng, setLng] = useState(dataCustomer ? dataCustomer.lng : 0);
    const [listContactAdd, setListContactAdd] = useState<ContactInterface[]>([]);
    const [listContactDel, setListContactDel] = useState<ContactInterface[]>([]);
    const [listContactModify, setListContactModify] = useState<ContactInterface[]>([]);
    const addContactFnRef = useRef<(() => void) | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const addressChanged = async (fullAddress: string) => {
        setAddress(fullAddress);
        const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}`;
        const headers = new Headers();
        headers.append("Accept", "application/json");
        try {
            const response = await fetch(url, {headers});
            const data = await response.json();
            let {features} = data;
            let res = features.map((e: { geometry: { coordinates: any; }; }) => {
                return {lng: e.geometry.coordinates[0], lat: e.geometry.coordinates[1]};
            });
            if (res.length >= 1) {
                let {lat, lng} = res[0];
                setLat(lat);
                setLng(lng);
            }
        } catch (err) {
            console.error(err);
        }
    };

    /**
     * Validation du formulaire
     */
    const handleSubmit = async () => {
        const errors: Record<string, string> = {};

        if (!inputName.trim()) errors.inputName = "La raison sociale est obligatoire.";
        if (inputSiret.trim() && !/^\d{14}$/.test(inputSiret.replace(/\s/g, ""))) errors.inputSiret = "Le SIRET doit contenir 14 chiffres.";

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error(Object.values(errors).join(" "), "Validation");
            return;
        }
        setFieldErrors({});

        let customer: CustomerInterface = {
            companyName: inputName,
            siret: inputSiret,
            address: inputAddress,
            id: dataCustomer ? dataCustomer.id : -1,
            userCnx: undefined,
            lat: inputLat,
            lng: inputLng
        };

        try {
            const contactPromises: Promise<any>[] = [];
            if (listContactAdd.length !== 0) {
                contactPromises.push(AddContact(getIdWithToken(), {
                    id_customer: dataCustomer ? dataCustomer.id : 0,
                    listContacts: listContactAdd
                }));
            }
            if (listContactDel.length !== 0) {
                for (let i = 0; i < listContactDel.length; i++) {
                    contactPromises.push(DeleteContact(getIdWithToken(), listContactDel[i]));
                }
            }
            if (listContactModify.length !== 0) {
                contactPromises.push(UpdateContact(getIdWithToken(), {listContacts: listContactModify}));
            }

            await Promise.all(contactPromises);

            if (dataCustomer) {
                await UpdateCustomer(getIdWithToken(), customer);
                toast.success("Client modifié avec succès.", "Client");
            } else {
                await AddCustomer(getIdWithToken(), customer);
                toast.success("Client ajouté avec succès.", "Client");
            }
            onFinished();
        } catch (err) {
            onError(err);
        }
    };

    const handlerEventContact = (listAdd: ContactInterface[], listDel: ContactInterface[], listModify: ContactInterface[]) => {
        setListContactAdd(listAdd);
        setListContactDel(listDel);
        setListContactModify(listModify);
    };

    return (
        <form className="fcaForm" onSubmit={(e) => e.preventDefault()}>
            <div className="fcaSection">
                <div className="fcaSectionLabel">Profil de l'entreprise</div>

                <div className={`fcaField${fieldErrors.inputName ? " fcaField--error" : ""}`}>
                    <label htmlFor="nom">Raison sociale</label>
                    <input id="nom" type="text" value={inputName} placeholder="e.g. Atelier Digital SARL"
                           onChange={(e) => setInputName(e.target.value)}/>
                    {fieldErrors.inputName && <span className="fcaFieldError">{fieldErrors.inputName}</span>}
                </div>

                <div className={`fcaField${fieldErrors.inputSiret ? " fcaField--error" : ""}`}>
                    <label htmlFor="siret">Numéro SIRET</label>
                    <input id="siret" type="text" value={inputSiret} placeholder="14 chiffres"
                           onChange={(e) => setSiret(e.target.value)}/>
                    {fieldErrors.inputSiret && <span className="fcaFieldError">{fieldErrors.inputSiret}</span>}
                </div>

                <div className="fcaField">
                    <label htmlFor="adresse">Adresse</label>
                    <textarea id="adresse" value={inputAddress}
                              placeholder="12 rue de la Paix, 75002 Paris"
                              onChange={(e) => addressChanged(e.target.value)}/>
                </div>
            </div>

            {dataCustomer && (
                <div className="fcaSection">
                    <div className="fcaSectionHeader">
                        <div className="fcaSectionLabel">Contacts &amp; interlocuteurs</div>
                        <button type="button" className="ctcAddBtn"
                                onClick={() => addContactFnRef.current?.()}>
                            <span className="material-symbols-outlined">person_add</span>
                            Ajouter un contact
                        </button>
                    </div>
                    {loading && <FCSpinner size="small"/>}
                    {error && <FCAlertMsgErrorAPI error={error}/>}
                    {!loading && contacts !== null && (
                        <TableContactCustomer
                            dataContact={contacts}
                            handlerEvent={handlerEventContact}
                            onAddContact={(fn) => { addContactFnRef.current = fn; }}
                        />
                    )}
                </div>
            )}

            <div className="fcaActions">
                <button type="button" className="fcaBtn fcaBtnSecondary" onClick={() => onFinished()}>
                    Annuler
                </button>
                <button type="button" className="fcaBtn fcaBtnPrimary" onClick={handleSubmit}>
                    <span className="material-symbols-outlined">check</span>
                    {dataCustomer ? "Enregistrer" : "Finaliser la création"}
                </button>
            </div>
        </form>
    );
};

export default FormCustomer;
